import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { Button } from '@/components/ui/Button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card'
import { ArrowLeft, Shield, AlertCircle, Plus } from 'lucide-react'
import { useToast } from '@/contexts/ToastContext'
import { useAuth } from '@/contexts/AuthContext'
import { listFirewallChangeRequests, markFirewallImplemented } from '@/lib/firewall'
import { FirewallChangeRequestRow } from '@/types'
import { supabase } from '@/lib/supabase'
import { Ship } from '@/types'
import { CreateFirewallChangeRequestModal } from '@/components/modals/CreateFirewallChangeRequestModal'
import { useTranslation } from 'react-i18next'

interface FirewallChangeRequestWithShip extends FirewallChangeRequestRow {
  ship?: Ship
}

export const FirewallOverviewPage: React.FC = () => {
  const { t, i18n } = useTranslation()
  const { user } = useAuth()
  const navigate = useNavigate()
  const { error, success } = useToast()
  const [firewallChangeRequests, setFirewallChangeRequests] = useState<FirewallChangeRequestWithShip[]>([])
  const [loading, setLoading] = useState(true)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const [hasLoaded, setHasLoaded] = useState(false)
  const [showCreateModal, setShowCreateModal] = useState(false)

  useEffect(() => {
    // only load once when component mounts
    if (!hasLoaded && user) {
      loadFirewallChangeRequests()
      setHasLoaded(true)
    }
  }, [user, hasLoaded])

  const loadFirewallChangeRequests = async () => {
    setLoading(true)
    setErrorMessage(null)
    try {
      // reduced timeout to 8 seconds
      const timeoutPromise = new Promise<never>((_, reject) => {
        setTimeout(() => reject(new Error('Request timeout - please check your connection and try again')), 8000)
      })
      
      const requests = await Promise.race([
        listFirewallChangeRequests(),
        timeoutPromise
      ])
      
      // fetch ship names in parallel with requests (if needed)
      const shipIds = [...new Set(requests.map(r => r.ship_id).filter(Boolean))]
      const shipsMap = new Map<string, Ship>()
      
      if (shipIds.length > 0) {
        // only fetch ship names, not all ship data
        const { data: ships, error: shipsError } = await supabase
          .from<Ship>('ships')
          .select('id, vessel_name')
          .in('id', shipIds)
        
        if (!shipsError && ships) {
          ships.forEach(ship => {
            shipsMap.set(ship.id, ship)
          })
        }
      }
      
      // combine requests with ship data
      const requestsWithShips: FirewallChangeRequestWithShip[] = requests.map(req => ({
        ...req,
        ship: shipsMap.get(req.ship_id)
      }))
      
      setFirewallChangeRequests(requestsWithShips)
    } catch (err: any) {
      console.error('Error loading firewall change requests:', err)
      const errorMsg = err.message || 'Failed to load firewall change requests'
      setErrorMessage(errorMsg)
      error('Error', errorMsg)
    } finally {
      setLoading(false)
    }
  }

  const formatDate = (dateString: string | null) => {
    if (!dateString) return '-'
    return new Date(dateString).toLocaleString(i18n.resolvedLanguage)
  }

  const getStatusBadgeColor = (status: FirewallChangeRequestRow['status']) => {
    switch (status) {
      case 'draft':
        return 'bg-slate-100 text-slate-700 dark:bg-slate-700 dark:text-slate-300'
      case 'under_review':
        return 'bg-amber-100 text-amber-700 dark:bg-amber-900 dark:text-amber-300'
      case 'approved':
        return 'bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300'
      case 'implemented':
        return 'bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300'
      case 'closed':
        return 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300'
      default:
        return 'bg-slate-100 text-slate-700 dark:bg-slate-700 dark:text-slate-300'
    }
  }

  const getApprovalStatusBadgeColor = (approvalStatus: FirewallChangeRequestRow['approval_status']) => {
    switch (approvalStatus) {
      case 'review':
        return 'bg-amber-100 text-amber-700 dark:bg-amber-900 dark:text-amber-300'
      case 'under_progress':
        return 'bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300'
      case 'approved':
        return 'bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300'
      case 'rejected':
        return 'bg-red-100 text-red-700 dark:bg-red-900 dark:text-red-300'
      default:
        return 'bg-slate-100 text-slate-700 dark:bg-slate-700 dark:text-slate-300'
    }
  }

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-900 text-slate-900 dark:text-white">
      {/* header */}
      <div className="border-b border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800">
        <div className="px-6 py-4 flex items-center gap-4">
          <Button variant="secondary" size="sm" onClick={() => navigate(-1)}>
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div className="flex-1">
            <h1 className="text-xl font-semibold flex items-center gap-2">
              <Shield className="h-6 w-6 text-blue-600 dark:text-blue-400" />
              {t('firewall.overviewTitle')}
            </h1>
            <p className="text-sm text-slate-500 dark:text-slate-400">
              {t('firewall.overviewSubtitle')}
            </p>
          </div>
          <Button onClick={() => setShowCreateModal(true)} variant="outline">
            <Plus className="h-4 w-4 mr-2" />
            {t('firewall.newRequest')}
          </Button>
        </div>
      </div>

      <div className="mx-auto px-4 sm:px-6 lg:px-8 py-8 max-w-7xl">
        {loading ? (
          <Card>
            <CardContent className="py-8 text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
              <p className="text-slate-600 dark:text-slate-400">{t('firewall.loading')}</p>
            </CardContent>
          </Card>
        ) : errorMessage ? (
          <Card>
            <CardContent className="py-8 text-center">
              <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
              <p className="text-red-600 dark:text-red-400">{errorMessage}</p>
              <Button onClick={loadFirewallChangeRequests} className="mt-4">
                {t('common.tryAgain')}
              </Button>
            </CardContent>
          </Card>
        ) : firewallChangeRequests.length === 0 ? (
          <Card>
            <CardContent className="py-8 text-center">
              <Shield className="h-12 w-12 text-slate-400 mx-auto mb-4" />
              <p className="text-slate-600 dark:text-slate-400 mb-4">{t('firewall.empty')}</p>
              <Button onClick={() => setShowCreateModal(true)} variant="outline">
                <Plus className="h-4 w-4 mr-2" />
                {t('firewall.createFirst')}
              </Button>
            </CardContent>
          </Card>
        ) : (
          <Card>
            <CardHeader>
              <CardTitle>{t('firewall.requests')}</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-slate-200 dark:border-slate-700">
                      <th className="text-left py-3 px-4 font-semibold text-slate-700 dark:text-slate-300">{t('moc.requestNo')}</th>
                      <th className="text-left py-3 px-4 font-semibold text-slate-700 dark:text-slate-300">{t('moc.vessel')}</th>
                      <th className="text-left py-3 px-4 font-semibold text-slate-700 dark:text-slate-300">{t('moc.requester')}</th>
                      <th className="text-left py-3 px-4 font-semibold text-slate-700 dark:text-slate-300">{t('fields.status')}</th>
                      <th className="text-left py-3 px-4 font-semibold text-slate-700 dark:text-slate-300">{t('moc.approvalStatus')}</th>
                      <th className="text-left py-3 px-4 font-semibold text-slate-700 dark:text-slate-300">{t('moc.implemented')}</th>
                      <th className="text-left py-3 px-4 font-semibold text-slate-700 dark:text-slate-300">{t('fields.createdAt')}</th>
                      <th className="text-left py-3 px-4 font-semibold text-slate-700 dark:text-slate-300">{t('fields.updatedAt')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {firewallChangeRequests.map((request) => (
                      <tr
                        key={request.id}
                        className="border-b border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-800"
                      >
                        <td 
                          className="py-3 px-4 cursor-pointer"
                          onClick={() => navigate(`/firewall/${request.id}`)}
                        >
                          <span className="font-medium">{request.request_no || '-'}</span>
                        </td>
                        <td 
                          className="py-3 px-4 cursor-pointer"
                          onClick={() => navigate(`/firewall/${request.id}`)}
                        >
                          {request.ship?.vessel_name || '-'}
                        </td>
                        <td 
                          className="py-3 px-4 cursor-pointer"
                          onClick={() => navigate(`/firewall/${request.id}`)}
                        >
                          {request.requester_name || '-'}
                        </td>
                        <td 
                          className="py-3 px-4 cursor-pointer"
                          onClick={() => navigate(`/firewall/${request.id}`)}
                        >
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusBadgeColor(request.status)}`}>
                            {t(`moc.status.${request.status}`, { defaultValue: request.status.replace('_', ' ') })}
                          </span>
                        </td>
                        <td 
                          className="py-3 px-4 cursor-pointer"
                          onClick={() => navigate(`/firewall/${request.id}`)}
                        >
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${getApprovalStatusBadgeColor(request.approval_status)}`}>
                            {t(`moc.approval.${request.approval_status}`, { defaultValue: request.approval_status.replace('_', ' ') })}
                          </span>
                        </td>
                        <td className="py-3 px-4" onClick={(e) => e.stopPropagation()}>
                          <input
                            type="checkbox"
                            checked={request.status === 'implemented'}
                            onChange={async (e) => {
                              e.stopPropagation()
                              if (e.target.checked && request.status !== 'implemented') {
                                if (!confirm(t('firewall.markImplementedConfirm'))) {
                                  e.target.checked = false
                                  return
                                }
                                const result = await markFirewallImplemented(request.id)
                                if (result.success) {
                                  success(t('moc.success'), t('firewall.markedImplemented'))
                                  loadFirewallChangeRequests()
                                } else {
                                  error(t('moc.error'), result.message || t('moc.markImplementedFailed'))
                                  e.target.checked = false
                                }
                              }
                            }}
                            disabled={request.status === 'closed' || request.status === 'implemented'}
                            className="w-4 h-4 cursor-pointer"
                            title={request.status === 'implemented' ? t('moc.alreadyImplemented') : t('moc.markImplemented')}
                          />
                        </td>
                        <td 
                          className="py-3 px-4 text-sm text-slate-600 dark:text-slate-400 cursor-pointer"
                          onClick={() => navigate(`/firewall/${request.id}`)}
                        >
                          {formatDate(request.created_at)}
                        </td>
                        <td 
                          className="py-3 px-4 text-sm text-slate-600 dark:text-slate-400 cursor-pointer"
                          onClick={() => navigate(`/firewall/${request.id}`)}
                        >
                          {formatDate(request.updated_at)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        )}
      </div>

      <CreateFirewallChangeRequestModal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onSuccess={() => {
          // reload only after successful creation
          setHasLoaded(false)
          loadFirewallChangeRequests()
          setHasLoaded(true)
        }}
      />
    </div>
  )
}

